import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/api';
import { confirmJobStart, confirmJobCompletion, createDirectHireOffer, selectBidders } from '../api/jobs';
import { useAuth } from '../context/AuthContext';
import { 
    FaMapMarkerAlt, FaMoneyBillWave, FaClock, FaUserTie, FaBriefcase, 
    FaCalendarAlt, FaCheckCircle, FaPlay, FaEnvelope, FaFlagCheckered, 
    FaArrowLeft, FaExclamationCircle, FaEdit, FaStar, FaHandshake
} from 'react-icons/fa';
import CountdownTimer from '../components/CountdownTimer';
import InfoModal from '../components/InfoModal';
import { Map, MapMarker } from '../components/ui/map';
import loadImage from '../assets/imageLoader';

function JobDetailsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form States
  const [applying, setApplying] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [showApplyForm, setShowApplyForm] = useState(false);

  // Bid States
  const [bidAmount, setBidAmount] = useState('');
  const [bidComments, setBidComments] = useState('');
  const [showBidForm, setShowBidForm] = useState(false);

  // Pagination & Bids Data 
  const [bids, setBids] = useState([]);
  const [totalBids, setTotalBids] = useState(0);
  const [bidPage, setBidPage] = useState(1);

  // Modal State 
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });

  const showInfoModal = (title, message) => {
    setModalContent({ title, message });
    setIsInfoModalOpen(true);
  };

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
  };

  const fetchJobDetails = useCallback(async () => {
    try {
      const response = await apiClient.get(`/jobs/${jobId}`);
      let jobData = response.data;
      
      // Robust Location Parsing
      let parsedLocation = { address: 'Location not specified', lat: null, lng: null };
      if (jobData.location) {
          if (typeof jobData.location === 'string') {
              try {
                  const jsonParsed = JSON.parse(jobData.location);
                  if (jsonParsed && typeof jsonParsed === 'object') {
                      parsedLocation = jsonParsed;
                  } else {
                      parsedLocation.address = jobData.location;
                  }
              } catch (e) {
                  parsedLocation.address = jobData.location;
              }
          } else if (typeof jobData.location === 'object') {
              parsedLocation = { ...parsedLocation, ...jobData.location };
          }
      }
      jobData.location = parsedLocation;

      setJob(jobData);
      setError('');
    } catch (err) {
      setError('Failed to load job details.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const fetchBids = useCallback(async () => {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/bids`, {
        params: { page: bidPage, limit: 15 }
      });
      setBids(response.data.bids);
      setTotalBids(response.data.total);
    } catch (err) {
      console.error('Failed to load bids:', err);
    }
  }, [jobId, bidPage]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  useEffect(() => {
    if (job && job.post_model === 'bidding') {
      fetchBids();
    }
  }, [job, fetchBids]);

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      await apiClient.post(`/jobs/${jobId}/apply`, { cover_letter: coverLetter });
      showInfoModal('Success', 'Application submitted successfully!');
      setShowApplyForm(false);
      setCoverLetter('');
      fetchJobDetails();
    } catch (err) {
      showInfoModal('Error', err.response?.data?.message || 'Failed to submit application.');
    } finally {
      setApplying(false);
    }
  };

  const handleBid = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const amount = parseFloat(bidAmount);
      if (amount > details.budget_per_worker) {
        showInfoModal('Error', `Bid amount cannot exceed the budget of ৳${details.budget_per_worker}`);
        setApplying(false);
        return;
      }
      await apiClient.post(`/jobs/${jobId}/bid`, {
        amount: parseFloat(bidAmount),
        comments: bidComments
      });
      showInfoModal('Success', 'Bid placed successfully!');
      setShowBidForm(false);
      setBidAmount('');
      setBidComments('');
      fetchBids();
    } catch (err) {
      showInfoModal('Error', err.response?.data?.message || 'Failed to place bid.');
    } finally {
      setApplying(false);
    }
  };

  const handleHireApplicant = async (applicant) => {
    if (!window.confirm(`Are you sure you want to hire ${applicant.name}? This will send them a job offer.`)) return;
    try {
        await createDirectHireOffer({
            title: job.title, // Just for consistency, API might ignore if existingJobId present
            description: job.description,
            workerProfileId: applicant.id,
            existingJobId: job.id,
            budget_per_worker: details.budget_per_worker, // Pass current budget
        });
        showInfoModal("Success", "Job offer sent! Waiting for worker to accept.");
        fetchJobDetails(); // Refresh to update status if needed
    } catch (err) {
        console.error(err);
        showInfoModal("Error", err.response?.data?.message || "Failed to send offer.");
    }
  };

  const handleAcceptBid = async (bid) => {
    if (!window.confirm(`Are you sure you want to accept the bid of ৳${bid.Bid_Amount} from ${bid.bidder.name}?`)) return;
    try {
        await selectBidders(job.id, [bid.bidder.id]);
        showInfoModal("Success", "Bid accepted! Winner selected.");
        fetchJobDetails();
        fetchBids();
    } catch (err) {
        console.error(err);
        showInfoModal("Error", err.response?.data?.message || "Failed to accept bid.");
    }
  };

  const handleConfirmStart = async () => {
    if (!window.confirm("Confirm that the worker has started the job?")) return;
    try {
      await confirmJobStart(jobId);
      showInfoModal("Success", "Job start confirmed!");
      fetchJobDetails();
    } catch (err) {
      showInfoModal("Error", "Failed to confirm start.");
    }
  };

  const handleConfirmCompletion = async () => {
    if (!window.confirm("Confirm that the job is complete and release payment?")) return;
    try {
      await confirmJobCompletion(jobId);
      showInfoModal("Success", "Job completion confirmed!");
      fetchJobDetails();
    } catch (err) {
      showInfoModal("Error", "Failed to confirm completion.");
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800"></div></div>;
  if (error) return <div className="p-10 text-center text-red-500 font-bold">{error}</div>;
  if (!job) return <div className="text-center mt-10">Job not found.</div>;

  const isWorker = user?.role === 'worker';
  const isBroker = user?.role === 'broker';
  const isOwner = user?.profileId === job.posted_by_id;
  const details = job.details?.[0] || {};
  const applications = details.applications || [];
  const isDirectHire = job.post_model === 'direct_apply';
  const activeContract = job.direct_hires?.[0];
  const contractStatus = activeContract?.status;

  // Helper: Check if a worker is already part of this job (offered or hired)
  const isHiredOrOffered = (applicantProfileId) => {
    const inDirectHires = job.direct_hires?.some(h => h.worker_id === applicantProfileId);
    const inSelected = job.selected_applicants?.some(s => s.applicant_profile_id === applicantProfileId);
    const inBidders = job.selected_bidders?.some(s => s.applicant_profile_id === applicantProfileId);
    return inDirectHires || inSelected || inBidders;
  };
  
  // Check if job is effectively closed for new hires
  const isJobActive = job.status === 'running' || job.status === 'completed' || contractStatus === 'active';

  const InfoItem = ({ icon: Icon, label, value, subValue }) => (
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
              <Icon size={20} />
          </div>
          <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
              <p className="font-bold text-slate-800">{value}</p>
              {subValue && <p className="text-xs text-slate-500">{subValue}</p>}
          </div>
      </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20">
      <InfoModal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} title={modalContent.title} message={modalContent.message} />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-8">
              <Link to={isOwner ? "/owner-dashboard" : "/jobs"} className="inline-flex items-center text-slate-400 hover:text-emerald-600 font-bold text-sm mb-6 transition-colors">
                  <FaArrowLeft className="mr-2" /> Back to Dashboard
              </Link>
              
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                      <div className="flex items-center gap-3 mb-2">
                          <h1 className="text-3xl md:text-4xl font-black text-slate-800 tracking-tight">{job.title}</h1>
                          <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${
                              job.status === 'open' ? 'bg-green-100 text-green-700' :
                              job.status === 'running' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                          }`}>
                              {job.status}
                          </span>
                      </div>
                      <p className="text-slate-500 font-medium flex items-center gap-2">
                          <FaMapMarkerAlt className="text-emerald-500" /> 
                          {job.location?.address || 'Location not specified'}
                      </p>
                  </div>
                  
                  <div className="text-right">
                      <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">Budget</p>
                      <p className="text-4xl font-black text-emerald-600">৳{details.budget_per_worker}</p>
                      <p className="text-xs font-bold text-emerald-600/70 uppercase">Per Worker</p>
                  </div>
              </div>
          </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Details */}
          <div className="lg:col-span-2 space-y-8">
              
              {/* Key Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoItem icon={FaBriefcase} label="Job Type" value={job.job_type === 'fixed_contract' ? 'Fixed Price' : 'Hourly'} />
                  <InfoItem 
                      icon={FaUserTie} 
                      label="Workers Status" 
                      value={`${job.activeHiresCount || 0} / ${details.required_workers} Hired`} 
                      subValue={details.required_workers - (job.activeHiresCount || 0) > 0 ? `${details.required_workers - (job.activeHiresCount || 0)} spots remaining` : 'Position Filled'}
                  />
                  
                  {isDirectHire ? (
                      <>
                          <InfoItem icon={FaCalendarAlt} label="Deadline" value={formatDate(job.direct_hires?.[0]?.Application_Deadline)} />
                          <InfoItem icon={FaClock} label="Duration" value={`${details.estimated_duration_hours || '-'} Hours`} />
                      </>
                  ) : (
                      <>
                          <InfoItem icon={FaClock} label="Bidding Ends" value={formatDate(details.bidding_sessions?.[0]?.end_time, true)} 
                              subValue={<CountdownTimer targetDate={details.bidding_sessions?.[0]?.end_time} />} 
                          />
                          <InfoItem icon={FaUserTie} label="Bids Placed" value={totalBids} />
                      </>
                  )}
              </div>

              {/* Description */}
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
                  <h3 className="text-lg font-black text-slate-800 mb-4 uppercase tracking-wide">Description</h3>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{job.description}</p>
              </div>

              {/* Map */}
              {job.location?.lat && job.location?.lng && (
                  <div className="bg-white p-2 rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                      <div className="h-80 rounded-2xl overflow-hidden relative z-0">
                          <Map 
                              center={[job.location.lng, job.location.lat]} 
                              zoom={14}
                              className="h-full w-full"
                          >
                              <MapMarker longitude={job.location.lng} latitude={job.location.lat} />
                          </Map>
                      </div>
                  </div>
              )}

              {/* Bids / Applicants List */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="p-6 border-b border-slate-50 flex justify-between items-center">
                      <h3 className="text-lg font-black text-slate-800 uppercase tracking-wide">
                          {isDirectHire ? `Applicants (${applications.length})` : `Bids (${totalBids})`}
                      </h3>
                      {isOwner && job.status === 'open' && (
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                              Select a candidate below
                          </span>
                      )}
                  </div>
                  
                  {isDirectHire ? (
                      applications.length > 0 ? (
                          <div className="divide-y divide-slate-50">
                              {applications.map(app => (
                                  <div key={app.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-slate-50 transition-colors gap-4">
                                      <div className="flex items-center gap-4">
                                          <img src={loadImage(app.applicant?.picture)} className="w-12 h-12 rounded-full object-cover bg-slate-100" alt="Applicant" />
                                          <div>
                                              <Link to={`/workers/profile/${app.applicant?.id}`} className="font-bold text-slate-800 hover:text-emerald-600 hover:underline">{app.applicant?.name || 'Unknown'}</Link>
                                              <p className="text-xs text-slate-500">{app.cover_letter || 'No cover letter'}</p>
                                          </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                          <span className="text-xs font-bold bg-slate-100 text-slate-500 px-3 py-1 rounded-full whitespace-nowrap">{formatDate(app.createdAt)}</span>
                                          {isOwner && job.status === 'open' && !isHiredOrOffered(app.applicant?.id) && (
                                              <button 
                                                  onClick={() => handleHireApplicant(app.applicant)}
                                                  className="px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200"
                                              >
                                                  Hire
                                              </button>
                                          )}
                                          {isOwner && isHiredOrOffered(app.applicant?.id) && (
                                              <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-3 py-2 rounded-xl uppercase tracking-widest">
                                                  Selected
                                              </span>
                                          )}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      ) : <div className="p-8 text-center text-slate-400 italic">No applicants yet.</div>
                  ) : (
                      bids.length > 0 ? (
                          <div className="divide-y divide-slate-50">
                              {bids.map(bid => (
                                  <div key={bid.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between hover:bg-slate-50 transition-colors gap-4">
                                      <div className="flex items-center gap-4">
                                          <img src={loadImage(bid.bidder?.picture)} className="w-12 h-12 rounded-full object-cover bg-slate-100" alt="Bidder" />
                                          <div>
                                              <Link to={`/workers/profile/${bid.bidder?.id}`} className="font-bold text-slate-800 hover:text-emerald-600 hover:underline">{bid.bidder?.name}</Link>
                                              <p className="text-xs text-slate-500">{formatDate(bid.updatedAt, true)}</p>
                                          </div>
                                      </div>
                                      <div className="text-right flex items-center gap-4 justify-end w-full sm:w-auto">
                                          <div>
                                              <p className="font-black text-emerald-600 text-lg">৳{bid.Bid_Amount || bid.amount}</p>
                                              {bid.comments && <p className="text-xs text-slate-400 max-w-[200px] truncate">{bid.comments}</p>}
                                          </div>
                                          {isOwner && job.status === 'bidding' && !isHiredOrOffered(bid.bidder?.id) && (
                                              <button 
                                                  onClick={() => handleAcceptBid(bid)}
                                                  className="px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-xl hover:bg-purple-700 transition-colors shadow-lg shadow-purple-200 whitespace-nowrap"
                                              >
                                                  Accept Bid
                                              </button>
                                          )}
                                          {isOwner && isHiredOrOffered(bid.bidder?.id) && (
                                              <span className="text-xs font-black text-purple-600 bg-purple-50 px-3 py-2 rounded-xl uppercase tracking-widest">
                                                  Selected
                                              </span>
                                          )}
                                      </div>
                                  </div>
                              ))}
                          </div>
                      ) : <div className="p-8 text-center text-slate-400 italic">No bids yet.</div>
                  )}
              </div>
          </div>

          {/* Right Column: Sidebar Actions */}
          <div className="space-y-6">
              
              {/* Status Actions (Buyer) */}
              {isOwner && (
                  <div className="bg-white p-6 rounded-3xl shadow-lg border border-emerald-100 sticky top-24">
                      <h3 className="font-bold text-slate-800 mb-4">Manage Job</h3>
                      
                      {contractStatus === 'pending_start' && (
                          <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100 mb-4">
                              <p className="text-xs font-bold text-yellow-800 mb-2 flex items-center"><FaExclamationCircle className="mr-1"/> Worker Ready to Start</p>
                              <button onClick={handleConfirmStart} className="w-full py-3 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 transition-colors shadow-md">Confirm Start</button>
                          </div>
                      )}
                      
                      {contractStatus === 'pending_end' && (
                          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-4">
                              <p className="text-xs font-bold text-blue-800 mb-2 flex items-center"><FaExclamationCircle className="mr-1"/> Work Completed</p>
                              <button onClick={handleConfirmCompletion} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-md">Confirm & Pay</button>
                          </div>
                      )}

                      <Link to={`/publish-job/edit/${job.id}`} className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors flex items-center justify-center mb-2">
                          <FaEdit className="mr-2" /> Edit Job
                      </Link>

                      {(job.status === 'open' || job.status === 'bidding') && (
                          <button 
                              onClick={async () => {
                                  if (window.confirm("Are you sure you want to delete this job? Any held funds will be refunded to your wallet.")) {
                                      try {
                                          await apiClient.delete(`/jobs/${job.id}`);
                                          alert("Job deleted and funds refunded.");
                                          navigate('/owner-dashboard');
                                      } catch (err) {
                                          alert("Failed to delete job.");
                                      }
                                  }
                              }}
                              className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors flex items-center justify-center"
                          >
                              Delete Job
                          </button>
                      )}
                  </div>
              )}

              {/* Action Card (Worker/Broker) */}
              {!isOwner && (isWorker || isBroker) && (job.status === 'open' || job.status === 'bidding') && (
                  <div className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100 sticky top-24">
                      <h3 className="font-bold text-slate-800 mb-4">Interested?</h3>
                      
                      {isDirectHire ? (
                          job.hasApplied ? (
                              <button disabled className="w-full py-4 bg-slate-100 text-slate-400 rounded-2xl font-bold cursor-not-allowed">Applied</button>
                          ) : !showApplyForm ? (
                              <button onClick={() => setShowApplyForm(true)} className="w-full py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-xl shadow-emerald-200 hover:bg-emerald-700 transition-all hover:scale-105 active:scale-95">
                                  Apply Now
                              </button>
                          ) : (
                              <form onSubmit={handleApply} className="space-y-4">
                                  <textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} placeholder="Write a short cover letter..." className="w-full p-4 border border-slate-200 rounded-xl text-sm focus:ring-2 ring-emerald-500 outline-none" rows="4" required />
                                  <div className="flex gap-2">
                                      <button type="button" onClick={() => setShowApplyForm(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
                                      <button type="submit" disabled={applying} className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold shadow-md hover:bg-emerald-700">Submit</button>
                                  </div>
                              </form>
                          )
                      ) : (
                          !showBidForm ? (
                              <button onClick={() => setShowBidForm(true)} className="w-full py-4 bg-purple-600 text-white rounded-2xl font-bold shadow-xl shadow-purple-200 hover:bg-purple-700 transition-all hover:scale-105 active:scale-95">
                                  Place Bid
                              </button>
                          ) : (
                              <form onSubmit={handleBid} className="space-y-4">
                                  <input type="number" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} placeholder="Bid Amount (৳)" className="w-full p-4 border border-slate-200 rounded-xl text-sm font-bold" required />
                                  <textarea value={bidComments} onChange={(e) => setBidComments(e.target.value)} placeholder="Comments (Optional)..." className="w-full p-4 border border-slate-200 rounded-xl text-sm" rows="2" />
                                  <div className="flex gap-2">
                                      <button type="button" onClick={() => setShowBidForm(false)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
                                      <button type="submit" disabled={applying} className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-bold shadow-md hover:bg-purple-700">Bid</button>
                                  </div>
                              </form>
                          )
                      )}

                      <button onClick={() => navigate('/messages', { state: { startChatWith: { id: job.posted_by_id, name: "Job Poster" } } })} className="w-full py-3 mt-4 border-2 border-slate-100 text-slate-600 rounded-2xl font-bold hover:border-emerald-500 hover:text-emerald-600 transition-all flex items-center justify-center">
                          <FaEnvelope className="mr-2" /> Message Employer
                      </button>
                  </div>
              )}

              {/* Poster Info */}
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wider">Posted By</h3>
                  <div className="flex items-center gap-4">
                      <img src={loadImage(job.poster?.picture)} className="w-14 h-14 rounded-full object-cover bg-slate-100" alt="Poster" />
                      <div>
                          <p className="font-black text-slate-800">{job.poster?.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500 font-bold">Verified Buyer</p>
                      </div>
                  </div>
              </div>

          </div>
      </div>
    </div>
  );
}

export default JobDetailsPage;